import type {
  Chat,
  ChatMessage,
  ChatStarterFilter,
  ChatSummary,
  ChatTab,
  HitKind,
  SearchHit,
  SearchResult,
} from "./types";
import { plainText } from "./answer/plain-text";

/**
 * Everything the Chats screens work out from the data, as plain functions of it and of `now`:
 * which day a chat falls under, what the row's time reads, which chats a filter keeps, and what a
 * search finds. Nothing here reads a clock or the screen, so a day can be named in a test.
 */

const DAY = 24 * 60 * 60 * 1000;

/** Calendar days between two moments, ignoring the time of day. 0 is today, 1 is yesterday. */
export function dayDiff(now: Date, at: Date): number {
  const start = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((start(now).getTime() - start(at).getTime()) / DAY);
}

export function clockTime(at: Date): string {
  const hours = at.getHours();
  const minutes = String(at.getMinutes()).padStart(2, "0");
  return `${String(hours % 12 === 0 ? 12 : hours % 12)}:${minutes} ${hours < 12 ? "AM" : "PM"}`;
}

const weekday = (at: Date, style: "short" | "long") =>
  at.toLocaleDateString("en-US", { weekday: style });
const monthName = (at: Date) => at.toLocaleDateString("en-US", { month: "long" });

/** A short date, "Oct 7". */
export function shortDate(at: Date): string {
  return at.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * How a row dates a chat: a clock time while it is today or yesterday (the group says which), a
 * weekday for the rest of the week, a date after that.
 */
export function timeLabel(at: Date, now: Date): string {
  const days = dayDiff(now, at);
  if (days <= 1) return clockTime(at);
  if (days < 7) return weekday(at, "short");
  return shortDate(at);
}

/**
 * How a line inside a chat is dated in search results: a clock time for today and yesterday, where
 * the chat's own heading says which, and a date for anything older.
 */
export function hitTimeLabel(at: Date, now: Date): string {
  return dayDiff(now, at) <= 1 ? clockTime(at) : shortDate(at);
}

/** What the open chat's header says about when it ran: "Oct 9 to Oct 14", or one day. */
export function dateRange(from: Date, to: Date): string {
  return dayDiff(to, from) === 0 ? shortDate(from) : `${shortDate(from)} to ${shortDate(to)}`;
}

export function summarize(chat: Chat): ChatSummary {
  const { files, steps: _steps, messages, stopped: _stopped, ...rest } = chat;
  const people = messages.filter((message) => message.author !== "assistant");
  const ask = people.at(-1);
  // Abacor's words as they read, not the syntax that draws them (a widget is its label here).
  const said = messages.findLast(
    (message) => message.author === "assistant" && message.text !== "",
  );
  const answer = said && { ...said, text: plainText(said.text) };
  return {
    ...rest,
    fileCount: files.length,
    peek: { ...(ask ? { ask } : {}), ...(answer ? { answer } : {}) },
  };
}

export type ChatFilters = { tab: ChatTab; starter: ChatStarterFilter };

export function filterChats<T extends ChatSummary>(
  chats: readonly T[],
  { tab, starter }: ChatFilters,
): T[] {
  return chats.filter((chat) => {
    if (starter !== "everyone" && chat.startedBy !== starter) return false;
    switch (tab) {
      case "all":
        return true;
      case "yours":
        return chat.startedBy === "me";
      case "needs-you":
        return chat.needsYou;
      case "draft":
        return chat.process?.state === "draft";
    }
  });
}

/** Newest first. */
export function sortChats<T extends ChatSummary>(chats: readonly T[]): T[] {
  return [...chats].sort((a, b) => b.lastAt.getTime() - a.lastAt.getTime());
}

export type ChatGroup = { key: string; label: string; chats: ChatSummary[] };

/**
 * The list under its day headings (Paper CH1). The last week is three groups that stay open; what
 * is older is grouped by month and folded away, the current month first.
 */
export function groupChats(
  chats: readonly ChatSummary[],
  now: Date,
): { recent: ChatGroup[]; earlier: ChatGroup[] } {
  const recent: ChatGroup[] = [
    { key: "today", label: `Today · ${weekday(now, "long")}`, chats: [] },
    { key: "yesterday", label: "Yesterday", chats: [] },
    { key: "week", label: "Last 7 days", chats: [] },
  ];
  const earlier: ChatGroup[] = [];
  for (const chat of sortChats(chats)) {
    const days = dayDiff(now, chat.lastAt);
    if (days < 7) {
      recent[days <= 0 ? 0 : days === 1 ? 1 : 2]?.chats.push(chat);
      continue;
    }
    const year = chat.lastAt.getFullYear();
    const month = chat.lastAt.getMonth();
    const key = `earlier-${String(year)}-${String(month)}`;
    let group = earlier.find((candidate) => candidate.key === key);
    if (!group) {
      const thisMonth = year === now.getFullYear() && month === now.getMonth();
      const label = thisMonth
        ? `Earlier in ${monthName(chat.lastAt)}`
        : year === now.getFullYear()
          ? monthName(chat.lastAt)
          : `${monthName(chat.lastAt)} ${String(year)}`;
      group = { key, label, chats: [] };
      earlier.push(group);
    }
    group.chats.push(chat);
  }
  return { recent: recent.filter((group) => group.chats.length > 0), earlier };
}

/** The words of a query, lowercase, each once. */
export function parseTerms(query: string): string[] {
  return [...new Set(query.toLowerCase().split(/\s+/).filter(Boolean))];
}

export type Segment = { text: string; match: boolean };

const escapePattern = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** The text cut into the parts a search matched and the parts it did not. */
export function highlight(text: string, terms: readonly string[]): Segment[] {
  if (!text) return [];
  if (terms.length === 0) return [{ text, match: false }];
  // Longest first, so "purchase order" marks as one word when both are terms, not "purchase" alone.
  const pattern = new RegExp(
    [...terms]
      .sort((a, b) => b.length - a.length)
      .map(escapePattern)
      .join("|"),
    "gi",
  );
  const segments: Segment[] = [];
  let from = 0;
  for (const found of text.matchAll(pattern)) {
    if (found.index > from) segments.push({ text: text.slice(from, found.index), match: false });
    segments.push({ text: found[0], match: true });
    from = found.index + found[0].length;
  }
  if (from < text.length) segments.push({ text: text.slice(from), match: false });
  return segments;
}

const hasAll = (text: string, terms: readonly string[]) => {
  const lower = text.toLowerCase();
  return terms.every((term) => lower.includes(term));
};

/** The lines of a message a search can look in: its words, then each row of a table it carries. */
function messageLines(message: ChatMessage): string[] {
  const rows = (message.table?.rows ?? []).map((row) =>
    Object.values(row)
      .filter((cell) => cell.length > 0)
      .join(" · "),
  );
  return [message.author === "assistant" ? plainText(message.text) : message.text, ...rows];
}

/**
 * Every line, step and file in every chat that holds all the words of the query (Paper CH1b). The
 * words have to meet in one line: a message about Brightline and another about statements do not
 * add up to a search for "brightline statements". Lines come back in the order they were said.
 */
export function searchChats(chats: readonly Chat[], query: string): SearchResult[] {
  const terms = parseTerms(query);
  if (terms.length === 0) return [];
  const results: SearchResult[] = [];
  for (const chat of chats) {
    const hits: SearchHit[] = [];
    for (const message of chat.messages) {
      const lines = messageLines(message);
      const index = lines.findIndex((line) => hasAll(line, terms));
      const line = lines[index];
      if (line === undefined) continue;
      hits.push({
        kind: "message",
        id: message.id,
        at: message.at,
        author: message.author,
        text: index === 0 || !message.table ? line : `${message.table.title}: ${line}`,
        asked: message.asked ?? false,
      });
    }
    for (const step of chat.steps) {
      if (hasAll(step.text, terms)) {
        hits.push({ kind: "step", id: step.id, at: step.at, app: step.app, text: step.text });
      }
    }
    for (const file of chat.files) {
      const page = file.pages?.find((candidate) => hasAll(`${file.name} ${candidate.text}`, terms));
      if (page) {
        hits.push({
          kind: "file",
          id: file.id,
          at: file.at,
          name: file.name,
          page: page.page,
          quote: page.text,
        });
      } else if (hasAll(file.name, terms)) {
        hits.push({
          kind: "file",
          id: file.id,
          at: file.at,
          name: file.name,
          page: null,
          quote: null,
        });
      }
    }
    const titleMatch = hasAll(chat.title, terms);
    if (hits.length === 0 && !titleMatch) continue;
    hits.sort((a, b) => a.at.getTime() - b.at.getTime());
    results.push({ chat: summarize(chat), titleMatch, hits });
  }
  return results;
}

/** What a result counts for: its lines, or one for a chat found only by its title. */
const weight = (result: SearchResult) => Math.max(result.hits.length, 1);

/** The best match first: the chat with the most lines, then the newest. */
export function sortResults(results: readonly SearchResult[]): SearchResult[] {
  return [...results].sort(
    (a, b) => weight(b) - weight(a) || b.chat.lastAt.getTime() - a.chat.lastAt.getTime(),
  );
}

export type HitCounts = { total: number } & Record<`${HitKind}s`, number>;

export function hitCounts(results: readonly SearchResult[]): HitCounts {
  const counts: HitCounts = { total: 0, messages: 0, files: 0, steps: 0 };
  for (const result of results) {
    counts.total += weight(result);
    for (const hit of result.hits) counts[`${hit.kind}s`] += 1;
  }
  return counts;
}

/** The results narrowed to one kind of line, dropping a chat that has none of it left. */
export function onlyKind(results: readonly SearchResult[], kind: HitKind | null): SearchResult[] {
  if (kind === null) return [...results];
  return results
    .map((result) => ({ ...result, hits: result.hits.filter((hit) => hit.kind === kind) }))
    .filter((result) => result.hits.length > 0);
}
